// src/components/UserDashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { FaCalendarCheck, FaCommentDots, FaReply, FaSpinner, FaPlusSquare } from 'react-icons/fa';

const API_BASE = process.env.REACT_APP_API_URL;

const UserDashboard = () => {
  const [appointments, setAppointments] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [newAppointmentForm, setNewAppointmentForm] = useState({ name: '', phone: '', message: '' });
  const [newFeedbackMessage, setNewFeedbackMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const navigate = useNavigate();

  // Check authentication on component mount and redirect if needed
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userRole = localStorage.getItem('userRole');
    console.log('UserDashboard.js useEffect: Auth check - Token:', token ? 'exists' : 'null', 'User Role:', userRole);

    if (!token || userRole === 'admin') {
      console.warn('UserDashboard.js useEffect: User not authenticated or is admin. Redirecting to login.');
      navigate('/login', { state: { from: '/user-dashboard' }, replace: true });
    }
  }, [navigate]);

  // Function to fetch user's appointments
  const fetchUserAppointments = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await axios.get(`${API_BASE}/api/appointments/my`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      setAppointments(res.data);
      console.log('UserDashboard.js: User appointments fetched successfully.');
    } catch (err) {
      console.error('UserDashboard.js: Error fetching user appointments:', err.response?.data || err.message);
      setMessage('❌ Failed to load your appointments.');
    }
  }, []); // Dependency array is empty as API_BASE is a stable outer scope value

  // Function to fetch user's feedback
  const fetchUserFeedback = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await axios.get(`${API_BASE}/api/feedback/my`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      setFeedback(res.data);
      console.log('UserDashboard.js: User feedback fetched successfully.');
    } catch (err) {
      console.error('UserDashboard.js: Error fetching user feedback:', err.response?.data || err.message);
      setMessage('❌ Failed to load your feedback.');
    }
  }, []); // Dependency array is empty as API_BASE is a stable outer scope value

  // Initial data fetch on component mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userRole = localStorage.getItem('userRole');
    if (token && userRole !== 'admin') {
      setLoading(true);
      const loadAllUserData = async () => {
        await Promise.all([fetchUserAppointments(), fetchUserFeedback()]);
        setLoading(false);
      };
      loadAllUserData();
    } else {
      setLoading(false); // If not authenticated or is admin, no data to load for this component
    }
  }, [fetchUserAppointments, fetchUserFeedback]);

  // Handle new appointment submission
  const handleAppointmentSubmit = async (e) => {
    e.preventDefault();
    setMessage('');

    if (!newAppointmentForm.name.trim() || !newAppointmentForm.phone.trim() || !newAppointmentForm.message.trim()) {
      setMessage('All appointment fields are required.');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setMessage('Authentication error: Please log in to book an appointment.');
        return;
      }
      await axios.post(`${API_BASE}/api/appointments`, newAppointmentForm, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      setMessage('✅ Appointment request submitted successfully!');
      setNewAppointmentForm({ name: '', phone: '', message: '' });
      fetchUserAppointments();
    } catch (err) {
      console.error('Appointment submission failed:', err.response?.data || err.message);
      setMessage('❌ Appointment submission failed. Please try again.');
    }
  };

  // Handle new feedback submission
  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    setMessage('');

    if (!newFeedbackMessage.trim()) {
      setMessage('Feedback message cannot be empty.');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setMessage('Authentication error: Please log in to submit feedback.');
        return;
      }
      await axios.post(`${API_BASE}/api/feedback`, { message: newFeedbackMessage }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      setMessage('✅ Feedback submitted successfully!');
      setNewFeedbackMessage('');
      fetchUserFeedback();
    } catch (err) {
      console.error('Feedback submission failed:', err.response?.data || err.message);
      setMessage('❌ Feedback submission failed. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-cyan-600 to-blue-800 font-inter text-white">
        <FaSpinner className="animate-spin text-white text-4xl mr-3" />
        <p>Loading your dashboard...</p>
      </div>
    );
  }

  if (!localStorage.getItem('token') || localStorage.getItem('userRole') === 'admin') {
    return null; // Should be handled by UserProtectedRoute redirect
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-600 to-blue-800 font-inter text-white p-6">
      <div className="max-w-6xl mx-auto bg-white p-8 rounded-xl shadow-2xl bg-opacity-10 backdrop-filter backdrop-blur-lg border border-white/20">
        <h2 className="text-4xl font-bold mb-8 text-center text-white">Your Dashboard</h2>

        {message && (
          <div className={`p-4 mb-6 rounded-md ${message.startsWith('✅') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {message}
          </div>
        )}

        {/* New Appointment / Feedback Forms */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* New Appointment Form */}
          <div className="bg-white p-6 rounded-lg shadow-xl bg-opacity-10 backdrop-filter backdrop-blur-md border border-white/20">
            <h3 className="text-2xl font-bold mb-4 text-white flex items-center">
              <FaPlusSquare className="mr-2" /> Book New Appointment
            </h3>
            <form onSubmit={handleAppointmentSubmit} className="space-y-4">
              <input
                type="text"
                placeholder="Your Name"
                value={newAppointmentForm.name}
                onChange={(e) => setNewAppointmentForm({ ...newAppointmentForm, name: e.target.value })}
                className="w-full px-4 py-2 bg-white/20 border border-white/30 rounded text-white placeholder-gray-300 focus:ring-2 focus:ring-cyan-400"
                required
              />
              <input
                type="tel"
                placeholder="Phone Number"
                value={newAppointmentForm.phone}
                onChange={(e) => setNewAppointmentForm({ ...newAppointmentForm, phone: e.target.value })}
                className="w-full px-4 py-2 bg-white/20 border border-white/30 rounded text-white placeholder-gray-300 focus:ring-2 focus:ring-cyan-400"
                required
              />
              <textarea
                placeholder="Tell us about your painting needs..."
                value={newAppointmentForm.message}
                onChange={(e) => setNewAppointmentForm({ ...newAppointmentForm, message: e.target.value })}
                rows="4"
                className="w-full px-4 py-2 bg-white/20 border border-white/30 rounded text-white placeholder-gray-300 focus:ring-2 focus:ring-cyan-400"
                required
              ></textarea>
              <button
                type="submit"
                className="w-full bg-white text-cyan-700 font-bold py-2 rounded-full hover:bg-gray-200 transition duration-300"
              >
                Book Appointment
              </button>
            </form>
          </div>

          {/* New Feedback Form */}
          <div className="bg-white p-6 rounded-lg shadow-xl bg-opacity-10 backdrop-filter backdrop-blur-md border border-white/20">
            <h3 className="text-2xl font-bold mb-4 text-white flex items-center">
              <FaPlusSquare className="mr-2" /> Send New Feedback
            </h3>
            <form onSubmit={handleFeedbackSubmit} className="space-y-4">
              <textarea
                placeholder="Type your feedback message here..."
                value={newFeedbackMessage}
                onChange={(e) => setNewFeedbackMessage(e.target.value)}
                rows="6"
                className="w-full px-4 py-2 bg-white/20 border border-white/30 rounded text-white placeholder-gray-300 focus:ring-2 focus:ring-cyan-400"
                required
              ></textarea>
              <button
                type="submit"
                className="w-full bg-white text-cyan-700 font-bold py-2 rounded-full hover:bg-gray-200 transition duration-300"
              >
                Submit Feedback
              </button>
            </form>
          </div>
        </div>

        {/* User's Past Appointments and Feedback */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* User's Appointments List */}
          <div className="bg-white p-6 rounded-lg shadow-xl bg-opacity-10 backdrop-filter backdrop-blur-md border border-white/20">
            <h3 className="text-2xl font-bold mb-4 text-white">Your Appointments</h3>
            {appointments.length === 0 ? (
              <p className="text-gray-200">No appointments found. Book one above!</p>
            ) : (
              <ul className="space-y-4">
                {appointments.map((appt) => (
                  <li key={appt._id} className="bg-white/5 p-4 rounded-lg border border-white/10 shadow-inner">
                    <p className="font-semibold text-white">
                      <FaCalendarCheck className="inline-block mr-2 text-cyan-300" />
                      {appt.name} ({appt.phone}) - <span className="text-sm font-normal text-gray-300">{format(new Date(appt.createdAt), 'MMM dd,yyyy HH:mm')}</span>
                    </p>
                    <p className="text-sm text-gray-200 mt-2">
                      <span className="font-semibold">Your Message:</span> {appt.message}
                    </p>
                    {appt.reply && (
                      <div className="mt-3 p-3 bg-white/10 rounded-md border border-white/20">
                        <p className="font-semibold text-cyan-300 flex items-center">
                          <FaReply className="mr-2" /> Admin Reply:
                        </p>
                        <p className="text-sm text-gray-100">{appt.reply}</p>
                      </div>
                    )}
                    <p className={`mt-2 text-xs font-semibold ${appt.status === 'replied' ? 'text-green-300' : 'text-yellow-300'}`}>
                      Status: {appt.status.toUpperCase()}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* User's Feedback List */}
          <div className="bg-white p-6 rounded-lg shadow-xl bg-opacity-10 backdrop-filter backdrop-blur-md border border-white/20">
            <h3 className="text-2xl font-bold mb-4 text-white">Your Feedback</h3>
            {feedback.length === 0 ? (
              <p className="text-gray-200">No feedback submitted. Send some above!</p>
            ) : (
              <ul className="space-y-4">
                {feedback.map((fb) => (
                  <li key={fb._id} className="bg-white/5 p-4 rounded-lg border border-white/10 shadow-inner">
                    <p className="font-semibold text-white text-sm">
                      <FaCommentDots className="inline-block mr-2 text-cyan-300" />
                      Submitted: <span className="font-normal text-gray-300">{format(new Date(fb.createdAt), 'MMM dd,yyyy HH:mm')}</span>
                    </p>
                    <p className="text-sm text-gray-200 mt-2">
                      <span className="font-semibold">Your Message:</span> {fb.message}
                    </p>
                    {fb.reply && (
                      <div className="mt-3 p-3 bg-white/10 rounded-md border border-white/20">
                        <p className="font-semibold text-cyan-300 flex items-center">
                          <FaReply className="mr-2" /> Admin Reply:
                        </p>
                        <p className="text-sm text-gray-100">{fb.reply}</p>
                      </div>
                    )}
                    <p className={`mt-2 text-xs font-semibold ${fb.status === 'replied' ? 'text-green-300' : 'text-yellow-300'}`}>
                      Status: {fb.status.toUpperCase()}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Logout Link */}
        <div className="mt-12 text-center">
          <button
            onClick={() => navigate('/logout')}
            className="bg-red-600 text-white font-bold py-2 px-6 rounded-full hover:bg-red-700 transition duration-300"
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;